import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function VaBenefits() {
    const [benefits, setBenefits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [filters, setFilters] = useState({
        category: '',
        isActive: ''
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchBenefits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBenefits = async (page = 1) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page,
                limit: 50,
                ...(filters.category && { category: filters.category }),
                ...(filters.isActive && { isActive: filters.isActive }),
            });

            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/va-benefits/list?${queryParams}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                setBenefits(data.data.benefits || []);
                setPagination(data.data.pagination || {});
            } else {
                toast.error(data.message || 'Failed to fetch VA benefits');
            }
        } catch (error) {
            console.error('Error fetching VA benefits:', error);
            toast.error('Error fetching VA benefits');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (benefit) => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/va-benefits/${benefit._id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isActive: !benefit.isActive })
                }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success(`Benefit ${!benefit.isActive ? 'activated' : 'deactivated'}`);
                fetchBenefits(pagination.page || 1);
            } else {
                toast.error(data.message || 'Update failed');
            }
        } catch (error) {
            console.error('Error toggling benefit:', error);
            toast.error('Error toggling benefit');
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/va-benefits/${deleteId}`,
                { method: 'DELETE' }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success('VA benefit deleted successfully');
                setDeleteId(null);
                window.$?.('#modaldelete_vabenefit').modal('hide');
                fetchBenefits(pagination.page || 1);
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting benefit:', error);
            toast.error('Error deleting benefit');
        } finally {
            setDeleting(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchBenefits(1);
    };

    const handleClearFilters = () => {
        setFilters({ category: '', isActive: '' });
        setTimeout(() => fetchBenefits(1), 0);
    };

    const criteriaSummary = (benefit) => {
        if (!benefit.eligibilityRules?.length) return 'none (always Possibly Eligible)';
        return benefit.eligibilityRules
            .map((rule) => `${rule.field} ${rule.operator} ${Array.isArray(rule.value) ? rule.value.join('/') : rule.value}`)
            .join(' · ');
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">VA Benefits</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            <Link to="/vaBenefitsAdd" className="btn btn-blue">
                                Add Benefit
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {/* Filters */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <form onSubmit={handleSearch} className="row align-items-end">
                                <div className="col-md-3">
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="category"
                                        value={filters.category}
                                        onChange={handleFilterChange}
                                        placeholder="e.g., Disability"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label>Active</label>
                                    <select
                                        className="form-control"
                                        name="isActive"
                                        value={filters.isActive}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <button type="submit" className="btn btn-primary mr-2">
                                        Filter
                                    </button>
                                    <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                                        Clear
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : benefits.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '50px' }}>Order</th>
                                                    <th style={{ width: '130px' }}>Category</th>
                                                    <th>Title</th>
                                                    <th>Eligibility Criteria</th>
                                                    <th style={{ width: '100px' }}>Active</th>
                                                    <th style={{ width: '100px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {benefits.map((benefit) => (
                                                    <tr key={benefit._id}>
                                                        <td>{benefit.displayOrder}</td>
                                                        <td>{benefit.category}</td>
                                                        <td>{benefit.title}</td>
                                                        <td className="text-muted small">{criteriaSummary(benefit)}</td>
                                                        <td align="center">
                                                            <div className="toggle-switch">
                                                                <label className="switch">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={benefit.isActive}
                                                                        onChange={() => handleToggleActive(benefit)}
                                                                    />
                                                                    <span className="slider"></span>
                                                                </label>
                                                                <span className="toggle-label">
                                                                    {benefit.isActive ? 'On' : 'Off'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div align="center">
                                                                <Link to={`/vaBenefitsAdd/${benefit._id}`} className="crsr_pntr">
                                                                    <i className="fa-solid fa-pen mr-2" title="Edit" />
                                                                </Link>
                                                                <button
                                                                    className="crsr_pntr btn btn-link p-0"
                                                                    onClick={() => setDeleteId(benefit._id)}
                                                                    data-toggle="modal"
                                                                    data-target="#modaldelete_vabenefit"
                                                                >
                                                                    <i className="fa-solid fa-trash-can f_blue" title="Delete" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        {pagination.pages > 1 && (
                                            <nav className="mt-3">
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchBenefits(pagination.page - 1)}
                                                            disabled={pagination.page === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.pages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => fetchBenefits(i + 1)}
                                                            >
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchBenefits(pagination.page + 1)}
                                                            disabled={pagination.page === pagination.pages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>No VA benefits found</p>
                                        <Link to="/vaBenefitsAdd" className="btn btn-primary">
                                            Create your first benefit
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <div className="modal fade" id="modaldelete_vabenefit" tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Confirm Delete</h5>
                            <button type="button" className="close" data-dismiss="modal">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this VA benefit?</p>
                            <p className="text-muted small">
                                This removes it from future benefits report PDFs immediately. Leads who already
                                received a PDF keep the copy they were sent. Consider deactivating instead of
                                deleting.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-dismiss="modal">
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default VaBenefits
